// Single source of truth for the case studies shown on the landing page
// (OurProjectsLanding.vue) and on the detail pages (pages/projects/[slug].vue).
// Add a new project here and it shows up in both places.

export interface CaseStudyTech {
  name: string;
  icon: string;
}

export interface CaseStudyBlock {
  title: string;
  description: string;
}

export interface CaseStudy {
  slug: string;
  name: string;
  // Used in the landing list
  short_description: string;
  image: string;
  link: string;
  // Detail page
  tagline: string;
  year: string;
  industry: string;
  platforms: string[];
  services: string[];
  intro: string[];
  challenge: string[];
  approach: CaseStudyBlock[];
  outcome: string[];
  // Optional short stat lines. Left empty for now — drop real figures in here later.
  highlights?: string[];
  tech: CaseStudyTech[];
}

const TECH = {
  vue: { name: 'Vue', icon: '/assets/technologies/vuejs.svg' },
  react: { name: 'React', icon: '/assets/technologies/react.svg' },
  nuxt: { name: 'Nuxt', icon: '/assets/technologies/nuxt.svg' },
  typescript: { name: 'TypeScript', icon: '/assets/technologies/typescript.svg' },
  javascript: { name: 'JavaScript', icon: '/assets/technologies/javascript.svg' },
  sass: { name: 'Sass', icon: '/assets/technologies/sass.svg' },
  pinia: { name: 'Pinia', icon: '/assets/technologies/pinia.svg' },
  go: { name: 'Go', icon: '/assets/technologies/golang.svg' },
  swift: { name: 'Swift', icon: '/assets/technologies/swift.svg' },
  kotlin: { name: 'Kotlin', icon: '/assets/technologies/kotlin.svg' },
  laravel: { name: 'Laravel', icon: '/assets/technologies/laravel.svg' },
  php: { name: 'PHP', icon: '/assets/technologies/php.svg' },
  node: { name: 'Node.js', icon: '/assets/technologies/nodejs.svg' },
  mysql: { name: 'MySQL', icon: '/assets/technologies/mysql.svg' },
  graphql: { name: 'GraphQL', icon: '/assets/technologies/graphQl.svg' },
  aws: { name: 'AWS', icon: '/assets/technologies/aws.svg' },
  digitalOcean: { name: 'DigitalOcean', icon: '/assets/technologies/digitalOcean.svg' },
  firebase: { name: 'Firebase', icon: '/assets/technologies/firebase.svg' },
  figma: { name: 'Figma', icon: '/assets/technologies/figma.svg' },
} satisfies Record<string, CaseStudyTech>;

export const caseStudies: CaseStudy[] = [
  {
    slug: 'hommex',
    name: 'HommeX',
    short_description:
      'A modern real-estate platform and mobile app that connects buyers, sellers, and agencies with smart tools like mortgage and insurance calculators.',
    image: '/assets/case-studies/HommeX_landing.webp',
    link: 'https://hommex.co/',
    tagline: 'A real-estate platform people actually want to use.',
    year: '2024',
    industry: 'Real estate / PropTech',
    platforms: ['Web app', 'iOS', 'Android'],
    services: ['Product strategy', 'UI/UX design', 'Web development', 'Mobile apps', 'Backend & APIs'],
    intro: [
      'Most property sites feel like spreadsheets with photos bolted on. HommeX came to us wanting the opposite: a place where someone could go from "I think we need a bigger apartment" to a shortlist of homes they can genuinely afford, without juggling five tabs and a calculator app on the side.',
      'So we built the whole thing end to end. One web platform and two native apps, sitting on top of a backend that handles listings, agencies, payments, and the number-crunching that usually sends buyers running to their bank.',
    ],
    challenge: [
      'A property marketplace has three very different people using it, and they all want different things. Buyers want to search and not feel overwhelmed. Sellers and agencies want their listings to look good and reach the right people. And everyone wants to trust that what they are looking at is real and up to date.',
      'On top of that, the hard part of buying a home is rarely the browsing. It is the money. Can I borrow this much? What would the monthly payment look like? Do I need insurance, and what does that add? Most platforms leave you to figure that out alone, which is exactly where people stall.',
      'We needed all of that to live in one product, stay fast on a phone with a patchy connection, and still be something an agency could manage without a manual.',
    ],
    approach: [
      {
        title: 'One product, three surfaces',
        description:
          'The web app, the iOS app, and the Android app share the same backend and the same logic, so a listing looks and behaves the same whether you found it on your laptop at work or on your phone on the train. We built the web side in Nuxt and Vue and the apps natively, which kept everything feeling quick and at home on each device.',
      },
      {
        title: 'The calculators that close the gap',
        description:
          'The mortgage and insurance calculators are baked right into the listing flow instead of being a separate tool nobody finds. You can look at a home and immediately see what it would mean for your monthly budget. That one change turns idle browsing into an actual decision.',
      },
      {
        title: 'Search that respects your time',
        description:
          'We spent real effort on filtering and map search so people can narrow down by the things that actually matter to them and not drown in results. Saved searches and favourites mean you can pick up where you left off instead of starting from scratch every visit.',
      },
      {
        title: 'Tools agencies will actually use',
        description:
          'Agencies get a management side built for daily work: posting and editing listings, handling enquiries, and keeping track of their portfolio. We kept it plain and fast on purpose, because a tool that is a chore to use just does not get used.',
      },
    ],
    outcome: [
      'HommeX shipped as a connected system rather than three half-matching products. Buyers get a search that does not fight them and answers to the money questions in the same place they are browsing. Agencies get a back office that keeps up with them.',
      'Because the platform and the apps run on shared foundations, new features land everywhere at roughly the same time, and the whole thing is built to grow as more listings and agencies come on board.',
    ],
    highlights: [
      'Three platforms, one shared backend',
      'Web, iOS & Android, all native',
      'Mortgage & insurance calculators built into the listing flow',
      'Dedicated tools for agencies',
    ],
    tech: [
      TECH.vue,
      TECH.nuxt,
      TECH.typescript,
      TECH.sass,
      TECH.pinia,
      TECH.go,
      TECH.laravel,
      TECH.php,
      TECH.mysql,
      TECH.swift,
      TECH.kotlin,
      TECH.digitalOcean,
      TECH.figma,
    ],
  },
  {
    slug: 'volt-cards',
    name: 'Volt Cards',
    short_description:
      'NFC digital business cards that let you instantly share your contact details and online presence with just one tap.',
    image: '/assets/case-studies/VoltCards_landing.webp',
    link: 'https://voltcards.com/',
    tagline: 'Your whole introduction, in one tap.',
    year: '2024',
    industry: 'Networking / SaaS',
    platforms: ['Web app', 'NFC hardware'],
    services: ['Product design', 'Web development', 'Backend & APIs'],
    intro: [
      'Paper business cards have a short life. Half of them get lost before you are home, and the other half sit in a drawer until they are out of date. Volt Cards set out to replace the whole ritual with something you tap against a phone.',
      'We built the platform behind those cards: the part that decides what someone sees the moment they tap, and the dashboard where you control all of it.',
    ],
    challenge: [
      'The magic moment lasts about two seconds. Someone taps your card, a profile opens on their phone, and that is your first impression. If it is slow, ugly, or broken, the whole pitch falls apart.',
      'So the page that loads after a tap had to be fast and clean on any phone, with no app to install and no friction. Behind that, people needed an easy way to edit their details, swap links, and update their card whenever something changes, without ever touching the physical card again.',
    ],
    approach: [
      {
        title: 'A profile that loads before you blink',
        description:
          'Tapping a card opens a lightweight public profile that is built to render instantly, even on a mid-range phone on mobile data. Contact details, social links, and your online presence are all one tap away, and saving someone to your phone takes a single button.',
      },
      {
        title: 'Edit once, every card updates',
        description:
          'The card points at your profile, not the other way around. Change your job title or add a new link in the dashboard and every card you have ever handed out reflects it immediately. No reprints, ever.',
      },
      {
        title: 'A dashboard anyone can run',
        description:
          'We kept the control panel deliberately simple. Managing what your card shows should feel like editing a contact, not configuring software, so that is exactly how it works.',
      },
    ],
    outcome: [
      'Volt Cards turned a stack of disposable paper into one card you keep and a profile you actually maintain. The tap-to-share experience is quick enough to survive a real handshake, and the dashboard makes keeping it current effortless.',
      'The platform is built to scale from a single freelancer to a whole company rolling out cards across a team.',
    ],
    highlights: [
      'Tap to share, no app to install',
      'Profiles built to load instantly',
      'Edit once, every card updates',
      'Scales from one person to a whole team',
    ],
    tech: [TECH.vue, TECH.typescript, TECH.sass, TECH.laravel, TECH.php, TECH.mysql, TECH.figma],
  },
  {
    slug: 'track-my-insurance',
    name: 'Track My Insurance',
    short_description:
      'A software tool that lets clients easily track their insurance coverage, spending, and remaining limits in real time.',
    image: '/assets/case-studies/TrackMyInsurance_landing.webp',
    link: '#',
    tagline: 'Finally, an insurance policy you can actually read.',
    year: '2025',
    industry: 'Insurance / FinTech',
    platforms: ['Web app'],
    services: ['Product design', 'Web development', 'Backend & APIs'],
    intro: [
      'Insurance is one of those things people pay for and then never look at again, partly because the paperwork is written to be skimmed past. Track My Insurance was about flipping that: giving people a clear, running picture of what they are covered for and what they have already used.',
      'We built a tool that takes the dense detail of a policy and turns it into something you can understand at a glance.',
    ],
    challenge: [
      'A single policy hides a lot of moving parts. There are limits, sub-limits, what has been claimed, what is left, and renewal dates that sneak up on you. Most people only discover where they stand at the worst possible moment, when they are trying to make a claim.',
      'The job was to take all of that and make it readable. Not a wall of fine print, but a live view that answers the only questions people actually ask: am I covered for this, and how much have I got left?',
    ],
    approach: [
      {
        title: 'Coverage at a glance',
        description:
          'The dashboard leads with the things that matter: what is covered, how much of each limit is spent, and how much remains. The detail is still there when you want it, but you no longer have to dig through a PDF to find out where you stand.',
      },
      {
        title: 'Spending and limits in real time',
        description:
          'As claims and usage come in, the numbers update, so the remaining balance on a policy is always current. No more guessing whether you have already used up an allowance for the year.',
      },
      {
        title: 'Built to handle the messy bits',
        description:
          'Real policies are full of edge cases and exceptions. We modelled the data so the tool can represent how cover actually works, not a watered-down version of it, which keeps what people see honest and accurate.',
      },
    ],
    outcome: [
      'Track My Insurance takes something most people actively avoid and makes it approachable. Instead of opening a document and hoping, clients get a straight answer about their coverage and their remaining limits whenever they want one.',
      'The result is a tool that builds trust through clarity, and a foundation that can grow to cover more policy types over time.',
    ],
    highlights: [
      'Coverage and remaining limits at a glance',
      'Balances that update in real time',
      'Built to model real policy edge cases',
      'No more digging through a PDF',
    ],
    tech: [TECH.vue, TECH.typescript, TECH.sass, TECH.laravel, TECH.php, TECH.mysql, TECH.figma],
  },
  {
    slug: 'corporix',
    name: 'Corporix',
    short_description:
      "An AI platform that answers customer and employee questions instantly, trained on a company's own knowledge and embeddable anywhere.",
    image: '/assets/case-studies/Corporix_landing.webp',
    link: 'https://corporix.ai/',
    tagline: "Your company's knowledge, ready to answer in an instant.",
    year: '2026',
    industry: 'Artificial intelligence / SaaS',
    platforms: ['Web app', 'Embeddable widget'],
    services: ['Product strategy', 'UI/UX design', 'Web development', 'Backend & APIs', 'AI & RAG'],
    intro: [
      'Every company sits on a pile of knowledge: support docs, policies, the answers to the same questions people ask every day. Almost none of it is easy to reach the moment you actually need it. Corporix turns that pile into something that answers back.',
      "We built an AI platform that learns from a company's own material and uses it to answer questions instantly, whether that's a customer on the website or an employee asking about an internal policy.",
    ],
    challenge: [
      'Generic AI chatbots have a trust problem. They sound confident and are sometimes completely wrong, which is the last thing you want representing your company to a customer. An answer has to come from the company’s real knowledge, not from whatever the model guessed.',
      "Then there's scale and separation. The platform serves many companies at once, and each one's data has to stay entirely its own: fast to search, and impossible to leak into someone else's chatbot. And the customer-facing side had to drop into any website without a developer rebuilding their front end.",
    ],
    approach: [
      {
        title: 'Answers grounded in real knowledge',
        description:
          "Corporix uses retrieval-augmented generation: before the AI replies, it pulls the relevant passages from the company's own documents and answers from those. Responses stay tied to what the company actually knows instead of what the model imagines.",
      },
      {
        title: 'Two jobs, one platform',
        description:
          'The same system runs public chatbots, the support widget anonymous visitors talk to, and internal ones, where employees ask about HR, IT, or company docs. One platform, two very different audiences, handled cleanly.',
      },
      {
        title: 'A widget that just drops in',
        description:
          'The customer-facing chatbot is a single embeddable widget that loads from one line of script. No framework, no rebuild. It sits on any site and starts answering.',
      },
      {
        title: 'Multi-tenant from day one',
        description:
          'Every company gets its own isolated data, with answers streamed back in real time as they are generated. The architecture keeps tenants separated and quick even as more companies come on board.',
      },
    ],
    outcome: [
      'Corporix gives companies an AI that actually knows their business and answers from it, around the clock, on the website and inside the team. Instead of hunting through documents or waiting on a reply, people just ask and get an answer.',
      "It's positioned as the simpler, AI-native alternative to the heavyweight support suites, and it's built to scale to many companies without the data ever crossing streams.",
    ],
    highlights: [
      'Answers grounded in your own knowledge',
      'Public support and internal Q&A in one',
      'Embeds with a single line of script',
      'Real-time answers, fully multi-tenant',
    ],
    tech: [TECH.vue, TECH.typescript, TECH.pinia, TECH.sass, TECH.laravel, TECH.php, TECH.mysql, TECH.figma],
  },
  {
    slug: 'padli',
    name: 'Padli',
    short_description:
      'Our own padel booking platform: players find a free court, fill the empty seats and keep score, while clubs run the whole venue from one panel.',
    image: '/assets/case-studies/Padli_landing.webp',
    link: 'https://padli.app/',
    tagline: 'Every free court, in one place.',
    year: '2026',
    industry: 'Sports / Booking',
    platforms: ['Web app', 'iOS', 'Android', 'Venue dashboard'],
    services: ['Product strategy', 'UI/UX design', 'Web development', 'Mobile apps', 'Backend & APIs'],
    intro: [
      'Padli is not client work. It is a product we own and run ourselves, built because booking a padel court was still a phone call, a WhatsApp group and a lot of guessing about who was actually coming.',
      'We designed and built all of it: the player app, the native iOS and Android apps, the dashboard clubs use to run their venue, the marketing site, and the Go backend underneath.',
    ],
    challenge: [
      'Padel is always two against two, so a booking is only half the problem. The other half is finding three more people at roughly your level, and making sure everyone pays their share without someone chasing money afterwards.',
      'Clubs have their own list: courts, opening hours, peak and off-peak pricing, memberships, cancellations and no-shows, mostly handled by staff standing at a front desk with a phone in their hand. Generic sports-admin tools treat all of that as a desktop spreadsheet.',
      'And it all had to feel like a good consumer app rather than an admin tool, in the language each player actually speaks.',
    ],
    approach: [
      {
        title: 'Booking without the back and forth',
        description:
          'Players see real availability at clubs near them, with the price for that exact hour. Pick a time and book it. The system assigns the court, so nobody has to choose between identical ones.',
      },
      {
        title: 'Open matches that fill themselves',
        description:
          'Short a player? Open the empty seats and players at your level can take them. Joining holds only your share of the price, and the last seat taken confirms the match for everyone in it.',
      },
      {
        title: 'Everything after the booking',
        description:
          'Split the bill by seat, keep a live score that is on all four phones at once, and watch a level from 0 to 7 move with every result. Bookings carry a wallet pass, and push notifications cover reminders, join requests and waitlist openings.',
      },
      {
        title: 'A venue panel that works on a phone',
        description:
          'Clubs manage courts, schedules, pricing rules, memberships, players and policies from one dashboard, with reports on revenue, occupancy and peak hours. It is built mobile-first because on-site staff run the venue from their phones, and every change leaves an audit trail.',
      },
    ],
    outcome: [
      'Padli turns organising a game from a group-chat chore into a couple of taps, and gives clubs one place to run the venue instead of a stack of spreadsheets and phone calls.',
      'Because we own it end to end, it is also where we push our own standards furthest: mobile-first everywhere, multilingual from day one, and a product that respects its users, with no dark patterns and no friction around cancelling.',
    ],
    highlights: [
      'Our own product, built and run in-house',
      'Player app, venue dashboard, iOS & Android',
      'Open matches that confirm themselves',
      'Live scoring, split payments and player levels',
    ],
    tech: [
      TECH.vue,
      TECH.nuxt,
      TECH.typescript,
      TECH.sass,
      TECH.pinia,
      TECH.go,
      TECH.mysql,
      TECH.swift,
      TECH.kotlin,
      TECH.firebase,
      TECH.digitalOcean,
      TECH.figma,
    ],
  },
  {
    slug: 'leadify-it',
    name: 'Leadify IT',
    short_description:
      'A drag-and-drop form and survey builder that helps businesses capture leads and turn responses into insight, with no developer required.',
    image: '/assets/case-studies/LeadifyIt_landing.webp',
    link: 'https://leadifyit.com/',
    tagline: 'Build a lead-capturing form in minutes, not meetings.',
    year: '2023',
    industry: 'Marketing technology / SaaS',
    platforms: ['Web app'],
    services: ['UI/UX design', 'Web development', 'Backend & APIs'],
    intro: [
      'Most teams that need a form end up waiting on a developer for something that should take five minutes. Leadify IT set out to remove that bottleneck: give marketers and small businesses a way to build a branded form, put it live, and start collecting leads the same afternoon.',
      'We built the platform that makes that possible, from the drag-and-drop builder down to the analytics that tell you whether a campaign is actually working.',
    ],
    challenge: [
      "A form builder lives or dies on two things that pull in opposite directions. It has to be simple enough that someone with zero technical background can build something in minutes, and flexible enough that it doesn't feel like a toy. Lean too far one way and it's frustrating; too far the other and it's overwhelming.",
      'And collecting responses is only half the job. The data has to come back in a shape people can read at a glance, the forms have to look like they belong to the brand using them, and everything has to work the same on a laptop or a phone.',
    ],
    approach: [
      {
        title: 'Drag, drop, done',
        description:
          'The builder uses ready-made fields you drag into place, so putting a form together feels closer to arranging slides than filling out a settings screen. No code, no template wrangling, just the fields you need in the order you want them.',
      },
      {
        title: 'Make it yours',
        description:
          'Colours, images, and the landing page around the form are all editable, so a form looks like it came from the company running it rather than from a template. That small thing makes a real difference to how many people finish it.',
      },
      {
        title: 'Numbers you can actually read',
        description:
          'Responses come back as live, visual analytics rather than a raw spreadsheet. You can see how a form is performing in real time and tell at a glance whether a campaign is pulling its weight.',
      },
      {
        title: 'Share it anywhere, together',
        description:
          'Forms embed straight into a website or go out as a link on social, and teams can work on the same account, so the whole thing fits into how people already work.',
      },
    ],
    outcome: [
      'Leadify IT turns a task that used to need a developer and a few days into something a marketer can do over a coffee. Build, brand, publish, and watch the responses land, all in one place.',
      'It scales the way its users do, from a single landing-page form to a business running campaigns across every channel.',
    ],
    highlights: [
      'Drag-and-drop, zero code',
      'On-brand forms and landing pages',
      'Real-time response analytics',
      'Embed anywhere or share a link',
    ],
    tech: [TECH.vue, TECH.typescript, TECH.sass, TECH.laravel, TECH.php, TECH.mysql, TECH.figma],
  },
  {
    slug: 'amb',
    name: 'AMB',
    short_description:
      'An accounting platform that gives businesses a clear, real-time handle on their books, invoicing, and day-to-day finances.',
    image: '/assets/case-studies/AMB_landing.webp',
    link: 'https://amb.mk/',
    tagline: 'Accounting that keeps up with the business, not the other way around.',
    year: '2023',
    industry: 'Accounting / FinTech',
    platforms: ['Web app'],
    services: ['UI/UX design', 'Web development', 'Backend & APIs'],
    intro: [
      'Accounting software has a reputation for being heavy, slow, and built for accountants rather than the people running the business. AMB wanted to change that: a system where the books, the invoices, and the numbers that matter all live in one place and are genuinely pleasant to use.',
      "We built the platform that handles the daily reality of a company's finances, from issuing an invoice to seeing exactly where the money stands at the end of the month.",
    ],
    challenge: [
      "Accounting is unforgiving. The numbers have to be right every single time, the rules are detailed and full of exceptions, and the people using the system are usually working against a deadline. There's no room for ‘close enough’.",
      'The challenge was to keep all of that rigour and still make it fast to work in. Raising an invoice or reconciling the month shouldn’t feel like wrestling with the software, even though what’s happening underneath is anything but simple.',
    ],
    approach: [
      {
        title: 'The whole picture in one place',
        description:
          "Invoicing, records, and financial overview live in a single system, so keeping the books doesn't mean jumping between tools and half a dozen spreadsheets.",
      },
      {
        title: 'Built for the rules',
        description:
          'We modelled the data around how accounting actually works, edge cases included, so the system stays accurate and trustworthy instead of approximating things to keep the interface tidy.',
      },
      {
        title: 'A straight answer on the money',
        description:
          'Beyond data entry, AMB answers the question every business owner asks: where do we stand right now? The financial picture is there to read, not buried at the bottom of a report.',
      },
      {
        title: 'Fast where it counts',
        description:
          'We put the effort into the tasks people do over and over, so the repetitive parts of bookkeeping are quick and the software gets out of the way.',
      },
    ],
    outcome: [
      'AMB takes the part of running a business that most people dread and makes it manageable. The books stay accurate, the daily work goes faster, and the financial picture is always within reach.',
      'It’s built to grow with the businesses that rely on it, taking on more complexity without becoming harder to use.',
    ],
    highlights: [
      'Invoicing and books in one place',
      'Accurate down to the edge cases',
      'A clear financial picture, any time',
      'Built for the work people repeat daily',
    ],
    tech: [TECH.laravel, TECH.php, TECH.mysql, TECH.react, TECH.typescript, TECH.sass, TECH.figma],
  },
];

export const getCaseStudyBySlug = (slug: string): CaseStudy | undefined =>
  caseStudies.find((caseStudy) => caseStudy.slug === slug);

// Featured projects shown on the home page, in this exact order.
// The full set still appears on the /projects page.
const FEATURED_ORDER = ['hommex', 'corporix', 'track-my-insurance'];

export const featuredCaseStudies: CaseStudy[] = FEATURED_ORDER.map((slug) =>
  caseStudies.find((caseStudy) => caseStudy.slug === slug)
).filter((caseStudy): caseStudy is CaseStudy => caseStudy !== undefined);
